import { useQueryClient } from "@tanstack/react-query";
import { Bot, ListTree, Sparkles } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetLanguages } from "~/api/languages/languages";
import {
  GetOrganizationSettings200PipelineMode,
  Language,
  UserRole,
} from "~/api/model";
import {
  getGetOrganizationQueryKey,
  getGetOrganizationSettingsQueryKey,
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganization,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { UpdateButton, UpdateButtonRef } from "~/components/UpdateButton";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { trackEvent } from "~/utils/analytics";

type PipelineMode = GetOrganizationSettings200PipelineMode;

type FormData = {
  language: Language;
};

export const SettingsOrganization: FC = () => {
  useTrackPageVisit("app/settings_organization/visit");

  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: organization } = useGetOrganization(organizationId);
  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const { data: languages } = useGetLanguages();
  const { mutate, isPending } = useUpdateOrganization();
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateOrganizationSettings();
  const updateButtonRef = useRef<UpdateButtonRef>(null);
  const queryClient = useQueryClient();

  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_TOC");

  const { handleSubmit, setValue, watch } = useForm<FormData>();

  const currentLanguage = watch("language");

  const onSubmit = (data: FormData) => {
    trackEvent({
      eventName: "app/settings_organization/language.select/change",
      properties: { language: data.language },
    });

    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.save",
      properties: { mode: selectedPipelineMode },
    });

    mutate(
      { organizationId, data: { language: data.language } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        },
      }
    );

    updateSettings(
      { organizationId, data: { pipelineMode: selectedPipelineMode } },
      {
        onSuccess: () => {
          updateButtonRef.current?.onSuccess();
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationSettingsQueryKey(organizationId),
          });
        },
      }
    );
  };

  const handlePipelineModeChange = (mode: PipelineMode) => {
    setSelectedPipelineMode(mode);
    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.select",
      properties: { mode },
    });
  };

  useEffect(() => {
    if (organization?.language) {
      setValue("language", organization.language);
    }
  }, [organization?.language, setValue]);

  useEffect(() => {
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(organizationSettings.pipelineMode);
    }
  }, [organizationSettings?.pipelineMode]);

  const handleLanguageChange = (value: string) => {
    setValue("language", value as Language);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-6 mt-2 w-[766px]"
    >
      <h3 className="text-2xl font-semibold">
        <FormattedMessage
          id="settings.organization"
          defaultMessage="Organization"
        />
      </h3>

      {/* Output Language Section */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="organizationLanguage">
          <FormattedMessage
            id="settings.organization.outputLanguage"
            defaultMessage="Output Language"
          />
        </Label>
        <Select value={currentLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger
            id="organizationLanguage"
            onClick={() => {
              trackEvent({
                eventName: "app/settings_organization/language.select/click",
              });
            }}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                id: "settings.organization.selectLanguage",
                defaultMessage: "Select a language",
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {languages &&
              Object.entries(languages).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.organization.outputLanguageHint"
            defaultMessage="This language will be used for AI-generated content such as summaries, classifications, and chat responses."
          />
        </span>
      </div>

      {/* Pipeline Mode Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[UserRole.OWNER, UserRole.SUPERADMIN]}>
        <div className="flex flex-col gap-4 border-t pt-6">
          <div>
            <Label>
              <FormattedMessage
                id="settings.organization.pipelineMode.title"
                defaultMessage="Document Processing Mode"
              />
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              <FormattedMessage
                id="settings.organization.pipelineMode.description"
                defaultMessage="Choose how Sky AI handles document structure and organization for new uploads."
              />
            </p>
          </div>

          <div className="grid gap-3">
            {/* Option 1: Use Table of Contents */}
            <div
              onClick={() => handlePipelineModeChange("USE_TOC")}
              className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${selectedPipelineMode === "USE_TOC" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            `}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handlePipelineModeChange("USE_TOC");
                }
              }}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 font-medium text-slate-900">
                      <ListTree className="h-4 w-4 text-slate-500" />
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useToc.title"
                        defaultMessage="Use Table of Contents"
                      />
                    </div>
                    <p className="text-slate-500 mt-1 text-xs">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useToc.description"
                        defaultMessage="Best for organized documents with clear hierarchies. Uses existing structure."
                      />
                    </p>
                    <p className="text-slate-400 mt-1 text-xs">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useToc.examples"
                        defaultMessage="Technical documentation, Policy manuals"
                      />
                    </p>
                    <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useToc.badge"
                        defaultMessage="Fastest processing"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Option 2: AI Processing Only */}
            <div
              onClick={() => handlePipelineModeChange("USE_SNOWFLAKE")}
              className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${selectedPipelineMode === "USE_SNOWFLAKE" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            `}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handlePipelineModeChange("USE_SNOWFLAKE");
                }
              }}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 font-medium text-slate-900">
                      <Sparkles className="h-4 w-4 text-orange-500" />
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useSnowflake.title"
                        defaultMessage="AI Processing Only"
                      />
                    </div>
                    <p className="text-slate-500 mt-1 text-xs">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useSnowflake.description"
                        defaultMessage="AI analyzes and organizes documents from scratch, ignoring existing structure."
                      />
                    </p>
                    <p className="text-slate-400 mt-1 text-xs">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useSnowflake.examples"
                        defaultMessage="Unstructured documents, Poor formatting, Legacy PDFs"
                      />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Option 3: Smart AI Agent */}
            <div
              onClick={() => handlePipelineModeChange("USE_SMART_AGENT")}
              className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${selectedPipelineMode === "USE_SMART_AGENT" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            `}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handlePipelineModeChange("USE_SMART_AGENT");
                }
              }}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 font-medium text-slate-900">
                      <Bot className="h-4 w-4 text-sky-500" />
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useSmartAgent.title"
                        defaultMessage="Smart AI Agent"
                      />
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                        <FormattedMessage
                          id="settings.organization.pipelineMode.useSmartAgent.recommended"
                          defaultMessage="Recommended"
                        />
                      </span>
                    </div>
                    <p className="text-slate-500 mt-1 text-xs">
                      <FormattedMessage
                        id="settings.organization.pipelineMode.useSmartAgent.description"
                        defaultMessage="AI Agent evaluates each document's quality and automatically chooses the best method."
                      />
                    </p>
                    <div className="mt-2 text-xs text-slate-600">
                      <p className="font-medium">
                        <FormattedMessage
                          id="settings.organization.pipelineMode.useSmartAgent.detects"
                          defaultMessage="The agent detects:"
                        />
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        <li className="flex items-center gap-1">
                          <span className="text-red-500">x</span>
                          <FormattedMessage
                            id="settings.organization.pipelineMode.useSmartAgent.detectsArtifacts"
                            defaultMessage="Code artifacts (SEC_001)"
                          />
                        </li>
                        <li className="flex items-center gap-1">
                          <span className="text-red-500">x</span>
                          <FormattedMessage
                            id="settings.organization.pipelineMode.useSmartAgent.detectsSplits"
                            defaultMessage="Page splits (Page 1, 2)"
                          />
                        </li>
                        <li className="flex items-center gap-1">
                          <span className="text-green-500">&#10003;</span>
                          <FormattedMessage
                            id="settings.organization.pipelineMode.useSmartAgent.detectsHierarchies"
                            defaultMessage="Clear hierarchies"
                          />
                        </li>
                        <li className="flex items-center gap-1">
                          <span className="text-green-500">&#10003;</span>
                          <FormattedMessage
                            id="settings.organization.pipelineMode.useSmartAgent.detectsNames"
                            defaultMessage="Descriptive names"
                          />
                        </li>
                      </ul>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      <p className="font-medium">
                        <FormattedMessage
                          id="settings.organization.pipelineMode.useSmartAgent.benefits"
                          defaultMessage="Benefits:"
                        />
                      </p>
                      <p>
                        <FormattedMessage
                          id="settings.organization.pipelineMode.useSmartAgent.benefitsList"
                          defaultMessage="Zero manual decisions, Adapts per document, Consistent quality, Review in Settings anytime"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DisplayFor>

      <UpdateButton
        isPending={isPending || isUpdatingSettings}
        ref={updateButtonRef}
      />
    </form>
  );
};
